'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ApplianceSpecs {
  weight?: number;
  weightEstimated?: boolean;
  weightNote?: string;
  width?: number;
  depth?: number;
  height?: number;
  source?: string;
  confidence: 'high' | 'medium' | 'low';
}

interface SpecLookupPanelProps {
  onWeightFound?: (weightKg: number) => void;
  onSpecsFound?: (specs: { weight?: number; width?: number; depth?: number; height?: number }) => void;
}

export default function SpecLookupPanel({ onWeightFound, onSpecsFound }: SpecLookupPanelProps) {
  const [modelName, setModelName] = useState('');
  const [loading, setLoading] = useState(false);
  const [specs, setSpecs] = useState<ApplianceSpecs | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    const trimmed = modelName.trim();
    if (trimmed.length < 2) return;

    setLoading(true);
    setError(null);
    setSpecs(null);

    try {
      const res = await fetch('/api/spec-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName: trimmed }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || '조회에 실패했습니다.');
        return;
      }

      if (data.success && data.data) {
        setSpecs(data.data);
        if (data.data.weight != null && onWeightFound) {
          onWeightFound(data.data.weight);
        }
        if (onSpecsFound) {
          onSpecsFound({
            weight: data.data.weight ?? undefined,
            width: data.data.width ?? undefined,
            depth: data.data.depth ?? undefined,
            height: data.data.height ?? undefined,
          });
        }
      }
    } catch {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && modelName.trim().length >= 2) {
      handleLookup();
    }
  };

  const fmt = (val: number) => val.toLocaleString();

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="text-sm font-medium text-gray-500">
        AI가 규격을 찾아 기본가를 계산해 드립니다.
      </div>

      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="모델명 입력 (예: 삼성전자 SL-C563W)"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-white"
          disabled={loading}
        />
        <Button
          onClick={handleLookup}
          disabled={loading || modelName.trim().length < 2}
          variant="outline"
          size="default"
        >
          {loading ? '조회 중...' : '규격 확인'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {specs && (
        <>
          {specs.confidence === 'low' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
              <p className="text-sm font-medium text-amber-800">
                규격 정보를 충분히 찾지 못했습니다.
              </p>
              <p className="text-xs text-amber-600">
                모델명을 정확히 입력했는지 확인해주세요.
              </p>
              {(specs.weight || specs.width || specs.depth || specs.height) && (
                <div className="text-sm text-amber-800 mt-2">
                  {specs.weight && <span>무게: {specs.weight}kg{specs.weightEstimated && ' (추정)'}</span>}
                  {specs.width && <span className="ml-3">폭: {fmt(specs.width)}mm</span>}
                  {specs.depth && <span className="ml-3">깊이: {fmt(specs.depth)}mm</span>}
                  {specs.height && <span className="ml-3">높이: {fmt(specs.height)}mm</span>}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-1">
              <div className="text-sm text-gray-800 space-y-0.5">
                {specs.weight != null && (
                  <div>
                    <p>
                      무게: <strong>{specs.weight}kg</strong>
                      {specs.weightEstimated && (
                        <span className="text-xs text-amber-600 ml-1">(추정)</span>
                      )}
                    </p>
                    {specs.weightEstimated && specs.weightNote && (
                      <p className="text-xs text-gray-400">{specs.weightNote}</p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-x-4">
                  {specs.width != null && (
                    <p>폭: <strong>{fmt(specs.width)}mm</strong></p>
                  )}
                  {specs.depth != null && (
                    <p>깊이: <strong>{fmt(specs.depth)}mm</strong></p>
                  )}
                </div>
                {specs.height != null && (
                  <p>높이: <strong>{fmt(specs.height)}mm</strong></p>
                )}
              </div>
              {specs.source && (
                <p className="text-xs text-gray-400 mt-1">
                  출처: {specs.source}
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            AI가 검색한 결과이며, 정확한 정보가 없을 경우에는 유사한 모델을 참고해 추산합니다.
          </p>
        </>
      )}
    </div>
  );
}
